/* eslint-disable import/no-cycle */

import moment from 'moment';
import { TFile } from './Storage.types';
import { TSADocPacketGet, TSADocPacketGetUI } from './AnimalTraining.types';
import {
  EAnimalPetChargeStatusDB,
  EAnimalPetChargeStatusUI,
  EApplicationStatus,
  EApplicationStatusUi,
  EGender,
  EGenderUi,
  EReproductive,
  EValueEditingStates,
  EVerificationStatus,
  EVerificationStatusUi,
  TEditingBooleanField,
  TEditingBooleanFieldUi,
  TEditingField,
  TEditingFieldUi,
  TExpirationDateType,
  TFail,
  TFailUI,
  TFileDataType,
  TFileDataTypeUI,
  TOptionType,
  TOptionTypeUi,
  TPropertyInfoUI,
  TPropertyManagementInfoUI,
  TStatusChangeInfoDB,
  TStatusChangeInfoUI,
  TStatusInfoWithSourceDB,
  TStatusInfoWithSourceUI,
  TVetRecordTypeUI,
  TSignatureType,
} from './GeneralTypes';
import {
  EVetRecordStatus,
  EVetRecordStatusUi,
  TApplicationVetRecords,
  TApplicationVetRecordsUI,
} from './VetRecord.types';
import {
  EAnimalTagDB,
  EAnimalTagUI,
  ETenantTagDB,
  ETenantTagUI,
  TAnimalDiscoveredTagsDB,
  TAnimalDiscoveredTagsUI,
} from './Tags.types';
import {
  TApplicationHPPacket,
  TApplicationHPPacketUI,
} from './Applications.types';
import type { TTenantDeactivationStatusUI } from './Tenant.types';
import { TAnimalApplicationTargetUI } from './AnimalActions.types';
import {
  TPropertyConfigSetGetDB,
  TPropertyConfigSetGetUI,
} from './propertyConfigSet.types';

export enum EAnimalActionDB {
  CONVERT = 'CONVERT',
}

export enum EAnimalActionUI {
  CONVERT = 'CONVERT',
}

export type TBlockedAction = {
  animalAction: EAnimalActionDB;
  reason: string;
};

export type TBlockedActionUI = {
  animalAction: EAnimalActionUI;
  reason: string;
};

export type TOptionTypeUI = {
  key: string;
  id: string;
  value: string;
};

export type TAnimalDetailObjectProps = {
  value: string;
  predefined: boolean;
};

export type TAnimalWeightProps = {
  value: number;
  unit: string;
};

export type TAnimalPostUI = {
  id: string;
  authorized: boolean;
  complete: boolean;
  name: string;
  profilePicture: string;
  type: string;
  species: string;
  breed?: string;
  weight?: number;
};

export type TAnimalGet = {
  id: string;
  authorized: boolean;
  complete: boolean;
  name: string;
  profilePicture: string;
  type: string;
  species: TAnimalDetailObjectProps;
  breed?: TAnimalDetailObjectProps;
  weight?: TAnimalWeightProps;
};

export type TGenderUI = { gender: EGenderUi; neutered: boolean };
export type TGender = { type: EGender; isReproductive: boolean };

export enum EAnimalTypesUI {
  PET = 'PET',
  ESA = 'ESA',
  SA = 'SA',
}

export enum EAnimalTypes {
  PET = 'PET',
  ESA = 'ESA',
  SA = 'SA',
}

export enum EAnimalPaymentStatusDB {
  PAID = 'PAID',
  MISSING = 'MISSING',
  MISSING_RENEWAL = 'MISSING_RENEWAL',
  SCHEDULED_RENEWAL = 'SCHEDULED_RENEWAL',
}

export enum EAnimalPaymentStatusUI {
  PAID = 'Paid',
  MISSING = 'Missing',
  MISSING_RENEWAL = 'Missing-Renewal',
  SCHEDULED_RENEWAL = 'Scheduled-Renewal',
}

export type TAnimalFormDataUI = {
  name: string;
  birthDate: moment.Moment;
  breed: TOptionTypeUi;
  species: TOptionTypeUi;
  primaryColor: TOptionTypeUi;
  secondaryColor1: TOptionTypeUi;
  secondaryColor2: TOptionTypeUi;
  weight: string;
  profilePicture: TFile | null;
  gender: EGenderUi;
  neutered: boolean;
  houseTrained: boolean;
  animalType: EAnimalTypesUI;
};

export type TAnimalFormData = {
  animalType: EAnimalTypes;
  name: string;
  birthDate: string;
  species: TOptionType;
  breed?: TOptionType | null;
  primaryColor: TOptionType;
  secondaryColor1: string | null;
  secondaryColor2: string | null;
  weight: string | null;
  profilePicture?: string | null;
  gender: TGender;
  houseTrained: boolean;
};

export type TCompleteAnimalFormDataUI = {
  name: string;
  birthDate: moment.Moment;
  breed: TOptionTypeUi;
  species: TOptionTypeUi;
  primaryColor: TOptionTypeUi;
  secondaryColor1: TOptionTypeUi;
  secondaryColor2: TOptionTypeUi;
  weight: string;
  profilePicture: TFile | null;
  gender: EGenderUi;
  neutered: boolean;
  houseTrained: boolean;
  animalId: string;
};

export type TCompleteAnimalFormData = {
  animalId: string;
  name: string;
  birthDate: string;
  species: TOptionType;
  breed?: TOptionType | null;
  primaryColor: TOptionType;
  secondaryColor1: string | null;
  secondaryColor2: string | null;
  weight: string | null;
  profilePicture?: string | null;
  gender: TGender;
  houseTrained: boolean;
};

export type TAnimalVetRecordsListUi = {
  documentFiles: TFileDataTypeUI[];
  expiration: TExpirationDateType;
  id: string;
  canEdit: boolean;
  type: string;
  status: EVetRecordStatusUi;
};

export type TAnimalVetRecordsList = {
  documentFiles: TFileDataType[];
  expiration: TExpirationDateType;
  id: string;
  locked?: boolean;
  type: EVetRecordStatus;
};

export type TConfirmPaymentUi = {
  animalId: string;
  leasePacketId: string;
};

export type TConfirmPayment = TConfirmPaymentUi;

export type TVetRecordTypeNameUpdate = {
  animalId: string;
  vetRecordId: string;
  type: TVetRecordTypeUI;
};

export type TVetRecordTypeNameUpdateUI = {
  animalId: string;
  vetRecordId: string;
  type: string;
};

export type TVetRecordExpirationDB = {
  vetRecordDateType: string;
  expirationDate: string;
};
export type TVetRecordExpirationUI = {
  vetRecordDateType: string;
  expirationDate: string;
};

export enum EVetRecordTypeUI {
  RABBIES = 'Rabbies',
  PARVO = 'Parvo',
  SPAYED_NEUTERED = 'Spayed/Neutered',
  DOG_LIABILITY_INSURANCE = 'Dog liability insurance',
  CITY_LICENSING = 'City Licensing',
}

export enum EVetRecordTypeDB {
  RABBIES = 'RABBIES',
  PARVO = 'PARVO',
  SPAYED_NEUTERED = 'SPAYED_NEUTERED',
  DOG_LIABILITY_INSURANCE = 'DOG_LIABILITY_INSURANCE',
  CITY_LICENSING = 'CITY_LICENSING',
}

export enum EVetRecordDateType {
  EXPIRED = 'expired',
  UNEXPIRED = 'unexpired',
}
export type TChangedRecords = {
  expiration: TExpirationDateType;
  id: string;
};
export type TVetRecordExpirationUpdate = {
  animalId: string;
  vetRecordId: string;
  changedRecords: TChangedRecords[];
};

export type TVetRecordExpirationUpdateUI = {
  animalId: string;
  recordId: string;
  vetRecordId: string;
  expirationDate: string;
  vetRecordDateType: EVetRecordDateType;
};

export type TUpdateAnimalTypeUI = {
  animalId: string;
  toType: EAnimalTypesUI;
  predefinedConversionReasonId?: string;
  reason?: string;
};

export type TUpdateAnimalTypeDB = {
  animalId: string;
  toType: EAnimalTypes;
  predefinedConversionReasonId?: string;
  reason?: string;
};

export type TApproveAnimalTypeConvUI = {
  animalId: string;
  animalConversionId: string;
};

export type TApproveAnimalTypeConvDB = {
  animalId: string;
  animalConversionId: string;
};

export type TRejectAnimalTypeConvUI = {
  animalId: string;
  animalConversionId: string;
  predefinedConversionReasonId?: string;
  reason?: string;
};

export type TRejectAnimalTypeConvDB = {
  animalId: string;
  animalConversionId: string;
  predefinedConversionReasonId?: string;
  reason?: string;
};

export type TVetRecordDocumentsUI = {
  animalId: string;
  vetRecordId: string;
  newFiles?: TFileDataTypeUI[];
  removedDocumentsId?: string[];
  removedRecordIds?: string[];
  newRecords?: TNewVetRecordUI[];
  changedRecords?: TNewVetRecordUI[];
};

export type TVetRecordDocuments = {
  animalId: string;
  vetRecordId: string;
  newFiles: TFile[];
  removedDocumentsId: string[];
  removedRecordIds?: string[];
  newRecords?: TNewVetRecordDB[];
  changedRecords?: TNewVetRecordDB[];
};

export type TNewVetRecordUI = {
  type: {
    predefined?: boolean;
    value?: string;
  };
  expiration?: TExpirationDateType;
  id?: string;
  isExpired?: boolean;
};

export type TNewVetRecordDB = {
  type: {
    predefined?: string;
    custom?: string;
    value?: string;
  };
  expiration?: TExpirationDateType;
  id?: string;
  isExpired?: boolean;
};

export type TAnimalInfoPropDB = {
  value: string;
  predefinedId?: string;
  predefined?: boolean;
};
export type TAnimalInfoPropUI = {
  value: string;
  predefinedId?: string;
  predefined?: boolean;
};

export type TAggressiveBehaviorDB = {
  acted: boolean;
  incidentDetails?: string;
};

export type TAggressiveBehaviorUI = {
  acted: string;
  incidentDetails?: string;
};

export type TAnimalBasicInfo = {
  birthDate: string;
  breed: TAnimalInfoPropDB | null;
  gender: EGender;
  id: string;
  isDeleted: boolean;
  isHouseTrained: boolean | null;
  isReproductive: boolean | null;
  name: string;
  photoGallery: string[];
  primaryColor: TAnimalInfoPropDB | null;
  profilePicture: string | null;
  secondaryColor1: TAnimalInfoPropDB | null;
  secondaryColor2: TAnimalInfoPropDB | null;
  species: TAnimalInfoPropDB | null;
  status: EVerificationStatus;
  type: EAnimalTypes;
  weight?: TAnimalWeightProps;
  microchip: string | null;
  isRequiredBecauseOfDisability?: boolean;
  trainedFor?: string;
  aggressiveBehavior: TAggressiveBehaviorDB | null;
};

export type TAnimalBasicInfoUi = {
  birthDate: string | null;
  breed: TAnimalInfoPropUI | null; //
  gender: EGenderUi;
  id: string;
  isDeleted: boolean;
  isHouseTrained: boolean | null;
  reproduction: EReproductive | null;
  isReproductive: boolean | null;
  name: string;
  photoGallery: string[];
  primaryColor: TAnimalInfoPropUI | null;
  profilePicture: string | null;
  secondaryColor1: TAnimalInfoPropUI | null;
  secondaryColor2: TAnimalInfoPropUI | null;
  species: TAnimalInfoPropUI | null;
  status: EVerificationStatusUi;
  type: EAnimalTypesUI;
  weight?: TAnimalWeightProps;
  microchip: string | null;
  isRequiredBecauseOfDisability?: boolean;
  trainedFor?: string;
  aggressiveBehavior: TAggressiveBehaviorUI | null;
};

export type TTenantDetail = {
  id: string;
  photo: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  deactivationStatusChangeInfo: TTenantDeactivationStatusUI | null;
};

export type TTenantDetailUi = {
  id: string;
  photo: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  deactivationStatusChangeInfo: TTenantDeactivationStatusUI | null;
};

export enum EResidenciesStatus {
  APPLICANT = 'APPLICANT',
  RESIDENT = 'RESIDENT',
  FUTURE = 'FUTURE',
  MOVED_OUT = 'MOVED_OUT',
  REJECTED = 'REJECTED',
  NOTICE = 'NOTICE',
}

export enum EResidenciesStatusUI {
  APPLICANT = 'Applicant',
  RESIDENT = 'Resident',
  FUTURE = 'Future Resident',
  MOVED_OUT = 'Former Resident',
  REJECTED = 'Rejected',
  NOTICE = 'Notice',
}

export enum EAuthorizationStatus {
  AUTHORIZED = 'AUTHORIZED',
}

export enum EAuthorizationStatusUi {
  AUTHORIZED = 'Authorized',
}
export type TPropertyManagerDB = {
  emailAddress: string;
  name: string;
  phone: string | null;
};
export type TPropertyManagerUI = {
  emailAddress: string;
  name: string;
  phone: string | null;
};
export type TChargeInformationDB = {
  status: EAnimalPetChargeStatusDB;
  changeInfo: TStatusInfoWithSourceDB | null;
  changeReason: string | null;
};

export type TChargeInformationUI = {
  status: EAnimalPetChargeStatusUI;
  changeInfo: TStatusInfoWithSourceUI | null;
  changeReason: string | null;
};

export type TAnimalPetRentDateDB = {
  startDate?: string;
  endDate?: string;
  startedAtOnboarding: boolean;
};
export type TAnimalPetRentDateUI = {
  startDate?: string;
  endDate?: string;
  startedAtOnboarding: boolean;
};

export type TEditChargeDatesUI = {
  animalId: string;
  tenantResidencyId: string;
  chargeType: TChargeType;
  chargeDate: Omit<TAnimalPetRentDateUI, 'startedAtOnboarding'>;
};

// PP-10057: why Re-Apply is unavailable, present only when it is. The app explains
// ANIMAL_TYPE_NOT_MANAGED and keeps hiding nothing else.
export enum ECanNotReapplyReason {
  ANIMAL_TYPE_NOT_MANAGED = 'ANIMAL_TYPE_NOT_MANAGED',
  OTHER = 'OTHER',
}

export type TAnimalResidencyDB = {
  id: string;
  status: EAnimalStatusDB;
  canNotReapplyReason: ECanNotReapplyReason | null;
  tags: null | EAnimalTagDB[];
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  discoveredTagReason: string | null;
  policyId: string | null;
  policyDateSigned: string | null;
  policySignedBy: string | null;
  policyContent: string | null;
  policyName: string | null;
  statusChangeInfo: TStatusChangeInfoDB | null;
  missingMoveInDateReason: string | null;
  moveInDate: string | null;
  signedDocType: TSignatureType | null;
  paymentStatus: EAnimalPaymentStatusDB | null;
  applicationStatus: EApplicationStatus;
  statusDetails: TAnimalStatusDetailsDB | null;
  moveInDatePassed: boolean;
  petRentChargingStatusDetails: TChargeInformationDB | null;
  petFeeChargingStatusDetails: TChargeInformationDB | null;
  applicationId?: string;
  petRentDate?: TAnimalPetRentDateDB;
  petFeeDate?: TAnimalPetRentDateDB;
};

export type TAnimalResidencyUI = {
  id: string;
  status: EAnimalStatusUI;
  canNotReapplyReason: ECanNotReapplyReason | null;
  tags: EAnimalTagUI[];
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  discoveredTagReason: string | null;
  policyId: string | null;
  policyDateSigned: string | null;
  policySignedBy: string | null;
  policyContent: string | null;
  policyName: string | null;
  statusChangeInfo: TStatusChangeInfoUI | null;
  missingMoveInDateReason: string | null;
  moveInDate: string | null;
  signedDocType: TSignatureType | null;
  paymentStatus: EAnimalPaymentStatusUI | null;
  applicationStatus: EApplicationStatusUi;
  splitStatus: TAnimalStatusDetailsUI;
  isMoveInDatePassed: boolean;
  petRentChargingStatusDetails: TChargeInformationUI | null;
  petFeeChargingStatusDetails: TChargeInformationUI | null;
  applicationId?: string;
  petRentDate?: TAnimalPetRentDateUI;
  petFeeDate?: TAnimalPetRentDateUI;
};

export enum EAnimalInfoConfig {
  ANIMAL_PHOTO = 'ANIMAL_PHOTO',
  ANIMAL_BIRTHDAY = 'ANIMAL_BIRTHDAY',
  ANIMAL_SPECIES = 'ANIMAL_SPECIES',
  ANIMAL_BREED = 'ANIMAL_BREED',
  ANIMAL_GENDER = 'ANIMAL_GENDER',
  ANIMAL_COLOR = 'ANIMAL_COLOR',
  ANIMAL_WEIGHT = 'ANIMAL_WEIGHT',
  MICROCHIP_NUMBER = 'MICROCHIP_NUMBER',
  SPAYED_NEUTERED = 'SPAYED_NEUTERED',
  HOUSE_TRAINED = 'HOUSE_TRAINED',
  HISTORY_OF_AGGRESSION = 'HISTORY_OF_AGGRESSION',
}

export type TAnimalRequirementsSetNoteDB = {
  id: string;
  title: string;
  description: string;
};
export type TAnimalRequirementsSetNoteUI = {
  id: string;
  title: string;
  description: string;
};

export type TAnimalRequirementsSetDB = {
  default: boolean;
  id: string;
  name: string;
  requirements: EAnimalInfoConfig[];
  tenantShouldPayForPet: boolean;
  notes: TAnimalRequirementsSetNoteDB[];
};
export type TAnimalRequirementsSetUI = {
  default: boolean;
  id: string;
  name: string;
  requirements: EAnimalInfoConfig[];
  tenantShouldPayForPet: boolean;
  notes: TAnimalRequirementsSetNoteUI[];
};

export type TResidencies = {
  id: string;
  animalResidency: TAnimalResidencyDB | null;
  authorizationStatus: EAuthorizationStatus;
  propertyName: string;
  unitNumber: string;
  propertyManagementBusinessName: string;
  propertyManagementBusinessLogo: string;
  leaseStartDate: string;
  notes: TNotesDB[];
  leaseToDate: string;
  policyId: string | null;
  status: EResidenciesStatus;
  isExpired: boolean;
  tags: ETenantTagDB[];
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  propertyManagers: TPropertyManagerDB[];
  statusChangeInfo: TStatusInfoWithSourceDB | null;
  city: string | null;
  state: string | null;
  statusUpdateLocked: boolean;
  landlordId: string;
  shouldTenantSignAddendum: boolean;
  animalVerificationConfiguration: TPropertyConfigSetGetDB | null;
  animalRestrictionStatus: EAnimalRestrictionStatus;
  animalRestrictionTags: string[];
  otherRestriction?: string;
  ticketId: string | null;
};

export type TResidenciesUi = {
  id: string;
  animalResidency: TAnimalResidencyUI | null;
  tags: ETenantTagUI[];
  canTagKnown: boolean;
  cannotTagKnownReason: string | null;
  authorizationStatus: EAuthorizationStatusUi;
  propertyName: string;
  unitNumber: string;
  propertyManagementBusinessName: string;
  propertyManagementBusinessLogo: string;
  leaseStartDate: string;
  notes: TNotesUI[];
  leaseToDate: string;
  policyId: string | null;
  status: EResidenciesStatusUI;
  isExpired: boolean;
  propertyManagers: TPropertyManagerUI[];
  statusChangeInfo: TStatusInfoWithSourceUI | null;
  city: string | null;
  state: string | null;
  statusUpdateLocked: boolean;
  landlordId: string;
  shouldTenantSignAddendum: boolean;
  animalRestrictionStatus: EAnimalRestrictionStatus;
  animalRestrictionTags: string[];
  otherReason?: string;
  animalRequirementsSet: TPropertyConfigSetGetUI | null;
  ticketId: string | null;
};
export type TNotesDB = {
  creationTime: string;
  creatorName: string;
  id: string;
  text: string;
};
export type TNotesUI = {
  creationTime: string;
  creatorName: string;
  id: string;
  text: string;
};
export type TAnimalApplicationRelatedPropertyManagers = {
  id: string;
  emailAddress: string | null;
  isLandlord: boolean;
  name: string;
  phoneNumber: string | null;
};

export type TAnimalApplicationRelatedPropertyManagersUi = {
  id: string;
  emailAddress: string | null;
  isLandlord: boolean;
  name: string;
  phoneNumber: string | null;
};

export type TAnimalApplication = {
  id: string;
  propertyName: string;
  unitNumber: string;
  propertyManagementBusinessName: string;
  propertyManagementBusinessLogo: string;
  propertyManagerName: string;
  leaseStartDate: string;
  leaseToDate: string | null;
  applicationStatus: EApplicationStatus;
  policyId: string | null;
  relatedPropertyManagers: TAnimalApplicationRelatedPropertyManagers[];
  landlord: Omit<TAnimalApplicationRelatedPropertyManagers, 'isLandlord'>;
  signedDocType?: TSignatureType | null;
};

export type TAnimalApplicationUi = {
  id: string;
  propertyName: string;
  unitNumber: string;
  propertyManagementBusinessName: string;
  propertyManagementBusinessLogo: string;
  propertyManagerName: string;
  leaseStartDate: string;
  leaseToDate: string | null;
  applicationStatus: EApplicationStatusUi;
  policyId: string | null;
  relatedPropertyManagers: TAnimalApplicationRelatedPropertyManagersUi[];
  landlord: Omit<TAnimalApplicationRelatedPropertyManagers, 'isLandlord'>;
  signedDocType?: TSignatureType | null;
};

export type TConversionType = {
  trainedFor?: string;
  trainerDocumentInfoNotProvidedReason: string | null;
  withdrawnRequest: boolean;
  hpDocumentInfoNotProvidedReason: string | null;
  observableDisability: boolean | null;
  createdAt: string;
  fromType: EAnimalTypes;
  hpDocumentPacket: TApplicationHPPacket | null;
  id: string;
  reason: string | null;
  saDocumentPacket: TApplicationSADocPacketGet | null;
  status: EConvertAnimalStatus;
  toType: EAnimalTypes;
  approveActorName: string | null;
};

export type TConversionTypeUI = {
  trainedFor?: string;
  trainerDocumentInfoNotProvidedReason: string | null;
  withdrawnRequest: boolean;
  hpDocumentInfoNotProvidedReason: string | null;
  observableDisability: boolean | null;
  createdAt: string;
  fromType: EAnimalTypesUI;
  hpDocumentPacket: TApplicationHPPacketUI | null;
  id: string;
  reason: string | null;
  saDocumentPacket: TApplicationSADocPacketGetUI | null;
  status: EConvertAnimalStatusUI;
  toType: EAnimalTypesUI;
  conversionApprover?: string;
  createdAtRaw: string;
};

export type TResidentTypeChange = {
  changedAt: string;
  fromType: EAnimalTypes;
  toType: EAnimalTypes;
  changedByName: string | null;
};

// A resident picking a different type during onboarding. Listed in the Conversion tab, but not a
// conversion: nobody reviewed it and it has no actions.
export type TResidentTypeChangeUI = {
  changedAt: string;
  changedAtRaw: string;
  fromType: EAnimalTypesUI;
  toType: EAnimalTypesUI;
  changedByName?: string;
};

export type TAnimalDetail = {
  hasObservableDisability: null | boolean;
  animal: TAnimalBasicInfo;
  tenant: TTenantDetail;
  residencies: TResidencies[] | null;
  applications: TAnimalApplication[] | null;
  vetRecords: TApplicationVetRecords[] | null;
  hpDocPackets: TApplicationHPPacket[] | null;
  saDocuments: TApplicationSADocPacketGet[] | null;
  conversions: TConversionType[] | null;
  residentTypeChanges?: TResidentTypeChange[] | null;
  blockedActions?: TBlockedAction[];
  isCompleteNote: boolean;
  statusChangeInfo: TStatusChangeInfoDB | null;
  missingNameReason: string | null;
  missingTypeReason: string | null;
  missingSpeciesReason: string | null;
  isToPetConverted: boolean;
  isConversionRemoved: boolean;
  conversionRemovalReason: string | null;
};

export type TAnimalDetailUi = {
  hasObservableDisability: null | boolean;
  animal: TAnimalBasicInfoUi;
  tenant: TTenantDetailUi;
  residencies: TResidenciesUi[] | null;
  applications: TAnimalApplicationUi[] | null;
  vetRecords: TApplicationVetRecordsUI[] | null;
  hpDocPackets: TApplicationHPPacketUI[] | null;
  saDocuments: TApplicationSADocPacketGetUI[] | null;
  conversions: TConversionTypeUI[] | null;
  residentTypeChanges: TResidentTypeChangeUI[];
  blockedActions?: TBlockedActionUI[];
  statusChangeInfo: TStatusChangeInfoUI | null;
  missingNameReason: string | null;
  missingTypeReason: string | null;
  missingSpeciesReason: string | null;
  isToPetConverted: boolean;
  isConversionRemoved: boolean;
  conversionRemovalReason: string | null;
};

export enum EConvertAnimalStatus {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
}

export enum EConvertAnimalStatusUI {
  APPROVED = 'Converted',
  REJECTED = 'Not approved',
  PENDING = 'Pending',
}

export type TAnimalWarningVerificationUI = {
  hasNotVerifiedDocument: boolean;
};

export type TAnimalWarningVerificationDB = {
  hasNotVerifiedDocument: boolean;
};

export type TAnimalType = {
  animalId: string;
  newType: string;
};

export type TAnimalTypeUI = {
  animalId: string;
  type: TOptionTypeUi;
};

export type TVetRecordUI = {
  animalId: string;
  vetRecordId: string;
};

export type TVetRecord = {
  animalId: string;
  vetRecordId: string;
};

export type TEditingWeightField = {
  value?: number | null;
  unit?: string | null;
  allowedOperations: EValueEditingStates[];
};

export type TEditingWeightFieldUi = {
  value: string | null;
  unit: string | null;
  canEdit: boolean;
  canRemove: boolean;
};

export type TAnimalDetailsGenderField = {
  value: EGender;
  allowedOperations: EValueEditingStates[];
};

export type TAnimalDetailsGenderFieldUi = {
  value: EGenderUi;
  canEdit: boolean;
  canRemove: boolean;
};

export type TAnimalDetails = {
  id: string;
  name: TEditingField;
  profilePicture: TEditingField;
  species: TEditingField;
  breed: TEditingField;
  primaryColor: TEditingField;
  secondaryColor1: TEditingField;
  secondaryColor2: TEditingField;
  birthDate: TEditingField;
  weight: TEditingWeightField;
  isHouseTrained: TEditingBooleanField;
  gender: TAnimalDetailsGenderField;
  isReproductive: TEditingBooleanField;
  vetRecords: TAnimalVetRecordsList[] | null;
  saDocumentPackets: TSADocPacketGet[] | null;
  type: EAnimalTypes;
};

export type TAnimalDetailsUi = {
  id: string;
  name: TEditingFieldUi;
  profilePicture: TEditingFieldUi;
  species: TEditingFieldUi;
  breed: TEditingFieldUi;
  primaryColor: TEditingFieldUi;
  secondaryColor1: TEditingFieldUi;
  secondaryColor2: TEditingFieldUi;
  birthDate: TEditingFieldUi;
  weight: TEditingWeightFieldUi;
  isHouseTrained: TEditingBooleanFieldUi;
  gender: TAnimalDetailsGenderFieldUi;
  isReproductive: TEditingBooleanFieldUi;
  vetRecordList: TAnimalVetRecordsListUi[];
  packets: TSADocPacketGetUI[];
  type: EAnimalTypesUI;
};

export type TAnimalFieldUpdateUi = {
  id: string;
  value: string;
};

export type TAnimalCreatableFieldUpdateUi = {
  id: string;
  value: TOptionTypeUi;
};

export type TAnimalCreateFieldUpdateUi = {
  id: string;
  value: string;
};

export type TAnimalNameUpdate = {
  animalId: string;
  name: string;
};
export type TAnimalNameUpdateUi = TAnimalFieldUpdateUi;
export type TAnimalHouseTrainedUpdate = {
  animalId: string;
  houseTrained: boolean;
};
export type TAnimalHouseTrainedUpdateUi = {
  id: string;
  value: boolean;
};
export type TAnimalGenderUpdate = {
  animalId: string;
  gender: EGender;
};
export type TAnimalGenderUpdateUi = {
  id: string;
  value: EGenderUi;
};
export type TAnimalProfileUpdate = {
  animalId: string;
  profilePicture: string;
};
export type TAnimalProfileUpdateUi = TAnimalFieldUpdateUi;
export type TAnimalWeightUpdate = {
  animalId: string;
  weight: string | null;
  unit: string;
};
export type TAnimalWeightUpdateUi = TAnimalFieldUpdateUi;
export type TAnimalBirthDate = {
  animalId: string;
  birthDate: string;
};
export type TAnimalBirthDateUi = TAnimalFieldUpdateUi;
export type TAnimalSecondaryColorUpdate = {
  animalId: string;
  colorId: string;
};
export type TAnimalSecondaryColorUpdateUi = TAnimalCreateFieldUpdateUi;
export type TAnimalReproductiveUpdate = {
  animalId: string;
  reproductive: boolean;
};
export type TAnimalReproductiveUpdateUi = {
  id: string;
  value: boolean;
};
export type TAnimalSpeciesUpdate = {
  animalId: string;
  species: TOptionType;
};
export type TAnimalSpeciesUpdateUi = TAnimalCreatableFieldUpdateUi;
export type TAnimalPrimaryColorUpdate = {
  animalId: string;
  primaryColor: TOptionType;
};
export type TAnimalPrimaryColorUpdateUi = TAnimalCreatableFieldUpdateUi;
export type TAnimalBreedUpdate = {
  animalId: string;
  breed: TOptionType;
};

export type TAnimalBreedUpdateUi = TAnimalCreatableFieldUpdateUi;
export type TAnimalSecondaryColor1Update = {
  animalId: string;
  colorId: string;
};

export type TApplicationSADocPacketGetUI = TSADocPacketGetUI & {
  fails?: TFailUI[];
};
export type TApplicationSADocPacketGet = TSADocPacketGet & { fails?: TFail };

export type TAnimalApplicationVetRecordsUI = TAnimalVetRecordsListUi & {
  fails: TFailUI[];
};
export type TAnimalApplicationVetRecords = TAnimalVetRecordsList & {
  fails?: TFail;
};

export type TApplicationAnimalUI = Omit<
  TAnimalDetailsUi,
  'vetRecordList' | 'packets'
> & {
  fails: TFailUI[];
  documentsFails?: TFailUI[];
  vetRecordList: TAnimalApplicationVetRecordsUI[];
  packets: TApplicationSADocPacketGetUI[];
};

export type TApplicationAnimal = Omit<
  TAnimalDetails,
  'vetRecords' | 'saDocumentPackets'
> & {
  fails?: TFail;
  vetRecords: TAnimalApplicationVetRecords[] | null;
  saDocumentPackets: TApplicationSADocPacketGet[] | null;
};

// The application half of the split animal status, exactly the backend's vocabulary. Kept
// apart from the legacy EResidencyStateDB/UI pair, which stays untouched on the wire.
export enum EAnimalApplicationStatusDB {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  NOT_APPROVED = 'NOT_APPROVED',
  ARCHIVED = 'ARCHIVED',
  RETRACTED = 'RETRACTED',
  CONDITIONALLY_APPROVED = 'CONDITIONALLY_APPROVED',
  ABANDONED = 'ABANDONED',
  APPROVED_UNTIL_RENEWAL = 'APPROVED_UNTIL_RENEWAL',
}

export enum EAnimalApplicationStatusUI {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  NOT_APPROVED = 'Not Approved',
  ARCHIVED = 'Archived',
  RETRACTED = 'Retracted',
  CONDITIONALLY_APPROVED = 'Conditionally Approved',
  ABANDONED = 'Abandoned',
  APPROVED_UNTIL_RENEWAL = 'Approved until Renewal',
}

export enum EAnimalResidencyTagUI {
  MOVED_IN = 'Moved In',
  MOVED_OUT = 'Moved Out',
}

export enum EAnimalResidencyTagDB {
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT = 'MOVED_OUT',
  NONE = 'NONE',
}

// Both halves of the split animal status, nested beside the legacy combined field exactly as
// the backend sends it.
export type TAnimalStatusDetailsDB = {
  applicationStatus: EAnimalApplicationStatusDB | null;
  residencyTag: EAnimalResidencyTagDB | null;
  applicationId: string | null;
  conditional: boolean;
  // Set only while the status is Approved until Renewal.
  approvedUntilRenewalDate: string | null;
};

// Kept nested on the UI side because the residency already carries an applicationStatus of a
// different vocabulary — the raw application status, not the one the status chip shows.
export type TAnimalStatusDetailsUI = {
  applicationStatus: EAnimalApplicationStatusUI | null;
  residencyTag: EAnimalResidencyTagUI | null;
  applicationId: string | null;
  conditional: boolean;
  approvedUntilRenewalDate: string | null;
};

export enum EResidencyStateDB {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  NONE = 'NONE',
  FUTURE = 'FUTURE',
  ABANDONED = 'ABANDONED',
}

export enum EResidencyStateUI {
  APPROVED = 'Moved In',
  PENDING = 'Pending',
  NONE = 'None',
  FUTURE = 'Future Animal',
  ABANDONED = 'Abandoned',
}
export enum EAnimalStatusDB { // === EAnimalResidencyStatusDB (PM side)
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  NOT_APPROVED = 'NOT_APPROVED',
  MOVED_OUT = 'MOVED_OUT',
  ARCHIVED = 'ARCHIVED',
  RETRACTED = 'RETRACTED',
  MOVED_IN = 'MOVED_IN',
  CONDITIONALLY_MOVED_IN = 'CONDITIONALLY_MOVED_IN',
  CONDITIONALLY_APPROVED = 'CONDITIONALLY_APPROVED',
  ABANDONED = 'ABANDONED',
}

export enum EAnimalStatusUI { // === EAnimalResidencyStatusUI (PM side)
  PENDING = 'Pending',
  APPROVED = 'Approved',
  NOT_APPROVED = 'Not approved',
  MOVED_OUT = 'Moved Out',
  ARCHIVED = 'Archived',
  RETRACTED = 'Retracted',
  MOVED_IN = 'Moved In',
  CONDITIONALLY_MOVED_IN = 'Conditionally Moved In',
  CONDITIONALLY_APPROVED = 'Conditionally Approved',
  ABANDONED = 'Abandoned',
}

export type TAnimalDB = {
  animalId: string;
  animalName: string;
  animalSpecie: string;
  animalSpecieId?: string;
  animalProfilePicture: string;
  animalType: EAnimalTypes;
  tenantName: string;
  tenantId: string;
  propertyManagementId: string | null;
  propertyManagementName: string | null;
  propertyManagementLogo: string | null;
  propertyId: string;
  propertyName: string;
  animalResidencyState: EResidencyStateDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  animalStatus: EVerificationStatus;
  creationTime: string;
  read: boolean;
  unitName: string | null;
  paymentStatus?: EAnimalPaymentStatusDB | null;
};

export type TAnimalInfoUI = {
  animalId: string;
  animalName: string;
  animalSpecie: string;
  animalSpeciePredefinedId?: string;
  animalProfilePicture: string;
  animalType: EAnimalTypesUI;
  animalResidencyState: EResidencyStateUI;
  applicationStatus: EAnimalApplicationStatusUI | null;
  approvedUntilRenewalDate: string | null;
  residencyTag: EAnimalResidencyTagUI | null;
  animalStatus: EVerificationStatusUi;
  firstLetters: string;
  paymentStatus?: EAnimalPaymentStatusUI | null;
};

export type TAnimalInfoDB = {
  animalId: string;
  animalName: string;
  animalSpecie: string;
  animalSpecieId?: string;
  animalProfilePicture: string;
  animalType: EAnimalTypes;
  animalResidencyState: EResidencyStateDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  animalStatus: EVerificationStatus;
  paymentStatus?: EAnimalPaymentStatusDB | null;
};
export type TTenantInfoDB = {
  tenantName: string;
  tenantId: string;
};
export type TTenantInfoUI = {
  tenantName: string;
  tenantId: string;
};

export type TAnimalUI = {
  animalInfo: TAnimalInfoUI;
  tenantInfo: TTenantInfoUI;
  propertyManagement: TPropertyManagementInfoUI;
  propertyInfo: TPropertyInfoUI;
  creationTime: string;
  read: boolean;
  unitNumber: string | null;
};

export enum ESortFields {
  creationTime = 'CREATION_DATE',
  animalInfo = 'NAME',
}

export enum ENotBeingChargedReasonDB {
  PET_FEE = 'PET_FEE',
  PET_RENT = 'PET_RENT',
}
export enum ENotBeingChargedReasonUI {
  PET_FEE = 'Pet Fee',
  PET_RENT = 'Pet Rent',
}

export type TChargeType = 'PET_FEE' | 'PET_RENT';

export type TChargingStatusUpdateUI = {
  tenantResidencyId: string;
  animalResidencyId: string;
  chargingStatus: EAnimalPetChargeStatusUI;
  reason?: string;
  shouldTagAnimalAsPNC?: boolean;
  notBeingChargedReason?: ENotBeingChargedReasonUI;
  chargeType: TChargeType;
  startDate?: string;
};

export type TChargingStatusUpdateDB = {
  tenantResidencyId: string;
  animalResidencyId: string;
  chargingStatus: EAnimalPetChargeStatusDB;
  reason?: string;
  shouldTagAnimalAsPNC?: boolean;
  notBeingChargedReason?: ENotBeingChargedReasonDB;
  chargeType: TChargeType;
  startDate?: string;
};

export type TMarkAsPNCDB = {
  tenantResidencyId: string;
  animalResidencyId: string;
  chargeType?: TChargeType;
};
export type TMarkAsPNCUI = {
  tenantResidencyId: string;
  animalResidencyId: string;
  chargeType?: TChargeType;
};

// KNOWN is keyed by animalId (not animal residency), so it works for applicant / future-resident
// animals that have no residency yet as well as moved-in animals.
export type TMarkKnownDB = {
  tenantResidencyId: string;
  animalId: string;
};
export type TMarkKnownUI = {
  tenantResidencyId: string;
  animalId: string;
};

export type TRemoveDiscoveredUI = {
  tenantResidencyId: string;
  animalId: string;
};
export type TRemoveDiscoveredDB = {
  tenantResidencyId: string;
  animalId: string;
};

export type TMarkAsDiscoverdUI = {
  tenantResidencyId: string;
  animalResidencyId: string;
  discoveredTagReason?: string;
  tag: TAnimalDiscoveredTagsUI;
};

export type TMarkAsDiscoverdDB = {
  tenantResidencyId: string;
  animalResidencyId: string;
  discoveredTagReason?: string;
  tag: TAnimalDiscoveredTagsDB;
};

export type TVetRecordsDB = {
  type: TVetRecordTypeUI;
  expiration?: TExpirationDateType;
};

export type TVetRecordsUI = {
  type: TVetRecordTypeUI;
  expiration?: TExpirationDateType;
};

export type TAnimalDetailVetRecordCreateUi = {
  animalId: string;
  files: TFileDataType[];
  // docName: string;
  records: TVetRecordsUI[];
};

export type TAnimalDetailVetRecordCreate = {
  animalId: string;
  records: TVetRecordsDB[];
  files: TFileDataType[];
  // docName: string;
};

export enum EVetRecordTypeEnumDB {
  'Rabies' = 'RABIES',
  'Spayed/Neutered' = 'SPAYED_NEUTERED',
  'Dog liability insurance' = 'DOG_LIABILITY_INSURANCE',
  'City Licensing' = 'CITY_LICENSING',
}

export enum EVetRecordTypeEnumUI {
  'PARVO' = 'Parvo',
  'RABIES' = 'Rabies',
  'SPAYED_NEUTERED' = 'Spayed/Neutered',
  'DOG_LIABILITY_INSURANCE' = 'Dog liability insurance',
  'CITY_LICENSING' = 'City Licensing',
}

export type TAnimalBreedDB = {
  value?: string;
  id?: string | null;
};
export type TAnimalBreedUI = {
  label: string;
  value: string;
};

// Animal info Updates START
export type TUpdateAnimalNameDB = {
  animalId: string;
  name: string;
};
export type TUpdateAnimalNameUI = {
  id: string;
  value: string;
};

export type TUpdateAnimalSpeciesDB = {
  animalId: string;
  animalSpecies: { id?: string; value?: string };
};
export type TUpdateAnimalSpeciesUI = {
  id: string;
  animalSpecies: TOptionTypeUi;
};
export type TUpdateAnimalBreedDB = {
  animalId: string;
  animalBreed: { id?: string; value?: string };
};
export type TUpdateAnimalBreedUI = {
  id: string;
  breed: TOptionTypeUi;
};
export type TUpdateAnimalMicrochipDateDB = {
  animalId: string;
  microchip: string;
};
export type TUpdateAnimalMicrochipDateUI = {
  id: string;
  value: string;
};

export type TUpdateAnimalWeightDB = {
  animalId: string;
  unit: string;
  weight: string;
};
export type TUpdateAnimalWeightUI = {
  id: string;
  value: string;
};

export type TUpdateAnimalBirthDateDB = {
  animalId: string;
  birthDate: string;
};
export type TUpdateAnimalBirthDateUI = {
  id: string;
  value: string;
};

export type TUpdateAnimalHouseTrainedDB = {
  animalId: string;
  houseTrained: boolean;
};
export type TUpdateAnimalHouseTrainedUI = {
  id: string;
  value: string;
};
export type TUpdateAnimalGenderDB = {
  animalId: string;
  gender: EGender;
};
export type TUpdateAnimalGenderUI = {
  id: string;
  value: EGenderUi;
};
export type TUpdateAnimalTrainedForDB = {
  animalId: string;
  trainedFor: string;
};
export type TUpdateAnimalTrainedForUI = {
  id: string;
  trainedFor: string;
};
export type TUpdateAnimalColorsDB = {
  animalId: string;
  primaryColor?: {
    value?: string;
    id?: string | null;
  };
  secondaryColor1?: string;
  secondaryColor2?: string;
};
export type TUpdateAnimalColorsUI = {
  id: string;
  primaryColor?: TOptionTypeUi;
  secondaryColor1?: TOptionTypeUi;
  secondaryColor2?: TOptionTypeUi;
};

export type TUpdateAnimalReproductiveDB = {
  animalId: string;
  reproductive: boolean;
};
export type TUpdateAnimalReproductiveUI = {
  id: string;
  value: string;
};

export type TUpdateAnimalProfilePictureDB = {
  animalId: string;
  profilePicture: string;
};
export type TUpdateAnimalProfilePictureUI = {
  id: string;
  profilePicture: string;
};

export type TUpdateAnimalAggressiveBehaviorDB = {
  animalId: string;
  aggressiveBehavior: { acted: boolean; incidentDetails?: string };
};
export type TUpdateAnimalAggressiveBehaviorUI = {
  id: string;
  aggressiveBehavior: { acted: boolean; incidentDetails?: string };
};
// Animal info Updates END

export type TAnimalColorsUI = {
  label: string;
  value: string;
  __isNew__: boolean;
};

export type TAnimalColorsDB = {
  value?: string;
  id: string | null;
};

// Animal Application Actions START
export type TApproveApplicationFormProps = {
  propertyId: string;
  unitNumber: string;
  moveInDate: string;
  leaseToDate: string;
};

export type TApproveApplicationFormPropsUI = {
  property: TOptionTypeUi;
  unit: TOptionTypeUi;
  moveInDate: string;
  leaseToDate: string;
};

export type TApproveApplicationUI = TAnimalApplicationTargetUI & {
  residenceId?: string;
  residencyDetails?: TApproveApplicationFormPropsUI;
  conditionalApproveDetails?: {
    dueToDate: string;
    reason: string;
  };
};

export type TApproveApplicationDB = TAnimalApplicationTargetUI & {
  residenceId?: string;
  residencyDetails?: TApproveApplicationFormProps;
  conditionalApproveDetails?: {
    dueDate: string;
    reason: string;
  };
};

// Animal Application Actions END

export enum EAnimalRestrictionStatus {
  RESTRICTED = 'RESTRICTED',
  NOT_RESTRICTED = 'NOT_RESTRICTED',
  NEED_MORE_INFORMATION = 'NEED_MORE_INFORMATION',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export type TChangeAnimalRestrictionState = {
  tenantResidencyId: string;
  animalId: string;
  animalRestrictionStatus: EAnimalRestrictionStatus;
  animalRestrictionTags: string[];
  otherRestriction?: string;
};

export type TPredefinedConversionReasonDB = {
  id: string;
  title: string;
  reason: string;
  templatePrefix: string | null;
  templateSuffix: string | null;
};

export type TPredefinedConversionReasonUI = {
  id: string;
  title: string;
  reason: string;
  templatePrefix: string | null;
  templateSuffix: string | null;
};

export enum EMoveOutOrRetractReasonTypeDB {
  ANIMAL_HAS_PASSED_AWAY = 'ANIMAL_HAS_PASSED_AWAY',
  ANIMAL_HAS_RE_HOMED = 'ANIMAL_HAS_RE_HOMED',
}

export enum EMoveOutOrRetractReasonTypeUI {
  ANIMAL_HAS_PASSED_AWAY = 'Animal has passed away',
  ANIMAL_HAS_RE_HOMED = 'Animal has been re-homed',
}

export enum EAnimalStatusUponDeletionUI {
  RESIDENT = 'RESIDENT',
  APPLICANT = 'APPLICANT',
}

export type TMoveOutReasonDB = {
  predefined?: EMoveOutOrRetractReasonTypeDB;
  other?: string;
};

export type TDeleteAnimalDB = {
  animalId: string;
  moveOutReason?: TMoveOutReasonDB;
  retractionReason?: string;
};

export type TDeleteAnimalUI = {
  animalId: string;
  moveOutReason?: EMoveOutOrRetractReasonTypeUI;
  otherReason?: string;
  retractionReason?: string;
};

export enum EAnimalHistoryActionDB {
  ANIMAL_CREATED = 'ANIMAL_CREATED',
  ANIMAL_DELETED = 'ANIMAL_DELETED',
  ANIMAL_COMPLETED = 'ANIMAL_COMPLETED',
  ANIMAL_NAME_UPDATED = 'ANIMAL_NAME_UPDATED',
  ANIMAL_WEIGHT_UPDATED = 'ANIMAL_WEIGHT_UPDATED',
  ANIMAL_SPECIE_UPDATED = 'ANIMAL_SPECIE_UPDATED',
  ANIMAL_BREED_UPDATED = 'ANIMAL_BREED_UPDATED',
  ANIMAL_NEW_PHOTO_ADDED = 'ANIMAL_NEW_PHOTO_ADDED',
  ANIMAL_PHOTO_REMOVED = 'ANIMAL_PHOTO_REMOVED',
  ANIMAL_BIRTHDAY_UPDATED = 'ANIMAL_BIRTHDAY_UPDATED',
  ANIMAL_GENDER_UPDATED = 'ANIMAL_GENDER_UPDATED',
  ANIMAL_REPRODUCTIVE_UPDATED = 'ANIMAL_REPRODUCTIVE_UPDATED',
  ANIMAL_HOUSE_TRAINED_UPDATED = 'ANIMAL_HOUSE_TRAINED_UPDATED',
  ANIMAL_PRIMARY_COLOR_UPDATED = 'ANIMAL_PRIMARY_COLOR_UPDATED',
  ANIMAL_NEW_VET_RECORD_ADDED = 'ANIMAL_NEW_VET_RECORD_ADDED',
  ANIMAL_VET_RECORD_UPDATED = 'ANIMAL_VET_RECORD_UPDATED',
  ANIMAL_VET_RECORD_REMOVED = 'ANIMAL_VET_RECORD_REMOVED',
  ANIMAL_NEW_HP_DOCUMENT_ADDED = 'ANIMAL_NEW_HP_DOCUMENT_ADDED',
  ANIMAL_HP_DOCUMENT_UPDATED = 'ANIMAL_HP_DOCUMENT_UPDATED',
  ANIMAL_HP_DOCUMENT_REMOVED = 'ANIMAL_HP_DOCUMENT_REMOVED',
  ANIMAL_NEW_SA_DOCUMENT_ADDED = 'ANIMAL_NEW_SA_DOCUMENT_ADDED',
  ANIMAL_SA_DOCUMENT_REMOVED = 'ANIMAL_SA_DOCUMENT_REMOVED',
  ANIMAL_SA_DOCUMENT_UPDATED = 'ANIMAL_SA_DOCUMENT_UPDATED',
  ANIMAL_CONVERSION_CREATED = 'ANIMAL_CONVERSION_CREATED',
  ANIMAL_CONVERTED = 'ANIMAL_CONVERTED',
  ANIMAL_HARD_CONVERTED = 'ANIMAL_HARD_CONVERTED',
  ANIMAL_CONVERSION_APPROVED = 'ANIMAL_CONVERSION_APPROVED',
  ANIMAL_VACCINATION_RECORD_EXPIRATION_UPDATED = 'ANIMAL_VACCINATION_RECORD_EXPIRATION_UPDATED',
  ANIMAL_VACCINATION_RECORD_REMOVED = 'ANIMAL_VACCINATION_RECORD_REMOVED',
  ANIMAL_VACCINATION_RECORD_TYPE_CHANGED = 'ANIMAL_VACCINATION_RECORD_TYPE_CHANGED',
  ANIMAL_VET_RECORD_DOCUMENT_FILE_ATTACHED = 'ANIMAL_VET_RECORD_DOCUMENT_FILE_ATTACHED',
  ANIMAL_VACCINATION_RECORD_ATTACHED = 'ANIMAL_VACCINATION_RECORD_ATTACHED',
  ANIMAL_VET_RECORD_DOCUMENT_FILE_REMOVE = 'ANIMAL_VET_RECORD_DOCUMENT_FILE_REMOVE',
  ANIMAL_OWNER_CHANGED = 'ANIMAL_OWNER_CHANGED',
  ANIMAL_AGGRESSIVE_BEHAVIOR_UPDATED = 'ANIMAL_AGGRESSIVE_BEHAVIOR_UPDATED',
  ANIMAL_TYPE_CONFIRMED = 'ANIMAL_TYPE_CONFIRMED',
  ANIMAL_TYPE_CHANGED = 'ANIMAL_TYPE_CHANGED',
  ANIMAL_MICROCHIP_NUMBER_UPDATE = 'ANIMAL_MICROCHIP_NUMBER_UPDATE',
  ANIMAL_VERIFIED = 'ANIMAL_VERIFIED',
  ANIMAL_VERIFICATION_RETRACTED = 'ANIMAL_VERIFICATION_RETRACTED',
  ANIMAL_VACCINATION_RECORD_VERIFIED = 'ANIMAL_VACCINATION_RECORD_VERIFIED',
  ANIMAL_VACCINATION_RECORD_VERIFICATION_REMOVED = 'ANIMAL_VACCINATION_RECORD_VERIFICATION_REMOVED',
  ANIMAL_HP_DOCUMENT_VERIFIED = 'ANIMAL_HP_DOCUMENT_VERIFIED',
  ANIMAL_HP_DOCUMENT_VERIFICATION_REMOVED = 'ANIMAL_HP_DOCUMENT_VERIFICATION_REMOVED',
  // PP-10351: decisions on the animal's applications, read from the application changelog
  ANIMAL_APPLICATION_CREATED = 'ANIMAL_APPLICATION_CREATED',
  ANIMAL_APPLICATION_STATUS_RESETTED = 'ANIMAL_APPLICATION_STATUS_RESETTED',
  ANIMAL_APPLICATION_REJECTED = 'ANIMAL_APPLICATION_REJECTED',
  ANIMAL_APPLICATION_ARCHIVED = 'ANIMAL_APPLICATION_ARCHIVED',
  ANIMAL_APPLICATION_APPROVED = 'ANIMAL_APPLICATION_APPROVED',
  CONDITIONALLY_APPROVED_APPLICATION_APPROVED = 'CONDITIONALLY_APPROVED_APPLICATION_APPROVED',
  ANIMAL_APPLICATION_RETRACTED = 'ANIMAL_APPLICATION_RETRACTED',
  ANIMAL_APPLICATION_ABANDONED = 'ANIMAL_APPLICATION_ABANDONED',
}

export enum EChangeSourceDB {
  SYSTEM = 'SYSTEM',
  RESMAN = 'RESMAN',
  YARDI = 'YARDI',
  REAL_PAGE = 'REAL_PAGE',
  APPFOLIO = 'APPFOLIO',
  HUB_SPOT = 'HUB_SPOT',
  FUNNEL = 'FUNNEL',
  ENTRATA = 'ENTRATA',
  SYNC_INTEGRATION = 'SYNC_INTEGRATION',
  PARTNER_API_INTEGRATION = 'PARTNER_API_INTEGRATION',
  SCIM = 'SCIM',
}

export type TAnimalHistoryItemDB = {
  actionType: EAnimalHistoryActionDB;
  occurredOn: string;
  actorName: string | null;
  source: EChangeSourceDB;
  description: string | null;
};

export type TAnimalHistoryItemUI = {
  action: string;
  occurredOn: string;
  actorName: string;
  source: string;
  description?: string;
};
